import type { ReactNode } from 'react';
import { Plus, ShieldCheck, Sparkle, X } from '@phosphor-icons/react';
import { CAPTION_MAX, CHANGE_KINDS, HASHTAGS_MAX, POST_PLATFORMS } from '@mybusinessmate/schema';
import type {
  CustomValues,
  MonthlyValues,
  Platform,
  PostValues,
  ReelValues,
  TaskFormErrors,
  TaskFormValues,
  WebsiteValues,
} from '../../lib/task-form';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { DateField, TimeField } from '../ui/date-field';
import { Input } from '../ui/input';
import { SegmentedControl } from '../ui/segmented-control';
import { Select } from '../ui/select';
import { TagInput } from '../ui/tag-input';
import { TextArea } from '../ui/text-area';
import { Icon } from '../Icon';

const PLATFORM_LABEL: Record<Platform, string> = { instagram: 'Instagram', facebook: 'Facebook page' };
// TODO(api): the design labels the Instagram option with the client's handle ("@paradisebiryani"); the profile
// keeps social links but not a structured handle to read it from here.

const CHANGE_LABEL: Record<(typeof CHANGE_KINDS)[number], string> = {
  text_prices: 'Text & prices',
  images: 'Images',
  layout: 'Layout',
  new_section: 'New section',
  fix: 'Fix something broken',
  other: 'Something else',
};

interface SectionProps<T> {
  value: T;
  onChange: (patch: Partial<T>) => void;
  errors: TaskFormErrors;
}

const fieldError = (errors: TaskFormErrors, name: string) => errors[`brief.${name}`];

function Counter({ used, max }: { used: number; max: number }) {
  return (
    <span className="text-body-xsmall text-text-tertiary">
      {used.toLocaleString('en-IN')} / {max.toLocaleString('en-IN')}
    </span>
  );
}

function Label({ children, aside }: { children: ReactNode; aside?: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-(--mbm-space-3)">
      <span className="text-label-medium text-text-primary">{children}</span>
      {aside}
    </div>
  );
}

function PlatformPicker({
  value,
  onChange,
  error,
}: {
  value: Platform[];
  onChange: (next: Platform[]) => void;
  error?: string;
}) {
  return (
    <fieldset className="flex flex-col gap-(--mbm-space-2)">
      <legend className="mb-(--mbm-space-2) text-label-medium text-text-primary">Post to</legend>
      <div className="flex flex-wrap gap-x-(--mbm-space-5) gap-y-(--mbm-space-2)">
        {POST_PLATFORMS.map((platform) => (
          <Checkbox
            key={platform}
            label={PLATFORM_LABEL[platform]}
            checked={value.includes(platform)}
            onChange={(e) => onChange(e.target.checked ? [...value, platform] : value.filter((p) => p !== platform))}
          />
        ))}
      </div>
      {error ? <p className="text-body-xsmall text-feedback-error">{error}</p> : null}
    </fieldset>
  );
}

function Hashtags({ value, onChange, error }: { value: string[]; onChange: (v: string[]) => void; error?: string }) {
  return (
    <div className="flex flex-col gap-(--mbm-space-2)">
      <Label
        aside={
          <span className="text-body-xsmall text-text-tertiary">
            {value.length} of {HASHTAGS_MAX}
          </span>
        }
      >
        Hashtags
      </Label>
      <TagInput variant="filled" value={value} onChange={onChange} placeholder="Type a hashtag and press Enter" />
      {error ? <p className="text-body-xsmall text-feedback-error">{error}</p> : null}
    </div>
  );
}

/** When it goes out: the date, the time and (optionally) a track. The client can move the date from their app. */
function PublishRow({
  date,
  time,
  audioNote,
  onChange,
  errors,
}: {
  date: string;
  time: string;
  audioNote: string;
  onChange: (patch: { publishDate?: string; publishTime?: string; audioNote?: string }) => void;
  errors: TaskFormErrors;
}) {
  return (
    <div className="flex flex-col gap-(--mbm-space-3) border-t border-border-line pt-(--mbm-space-5)">
      <div className="grid gap-(--mbm-space-3) sm:grid-cols-3">
        <DateField
          label="Publish on"
          showYear
          value={date}
          error={fieldError(errors, 'scheduledUploadAt')}
          onChange={(publishDate) => onChange({ publishDate })}
        />
        <TimeField label="Time" value={time} disabled={!date} onChange={(publishTime) => onChange({ publishTime })} />
        <Input
          label="Audio (optional)"
          variant="filled"
          leadingIcon={Sparkle}
          placeholder="e.g. Trending: Garba Beats"
          value={audioNote}
          error={fieldError(errors, 'audioNote')}
          onChange={(e) => onChange({ audioNote: e.target.value })}
        />
      </div>
      {/* TODO(api): auto-posting needs the Meta connection (Sprint 5); until then this is the planned time only. */}
      <p className="flex items-center gap-(--mbm-space-2) text-body-xsmall text-text-tertiary">
        <Icon icon={ShieldCheck} size="sm" />
        The client approves before anything is posted, and can move the date from their app.
      </p>
    </div>
  );
}

function PostSection({ value: v, onChange, errors }: SectionProps<PostValues>) {
  return (
    <>
      <div className="grid gap-(--mbm-space-5) sm:grid-cols-2">
        <SegmentedControl
          label="Format"
          options={[
            { value: 'single_image', label: 'Single image' },
            { value: 'carousel', label: 'Carousel' },
          ]}
          value={v.format}
          onChange={(format) => onChange({ format })}
        />
        <PlatformPicker
          value={v.platforms}
          onChange={(platforms) => onChange({ platforms })}
          error={fieldError(errors, 'platforms')}
        />
      </div>
      <TextArea
        label="Caption"
        variant="filled"
        value={v.caption}
        labelAside={<Counter used={v.caption.length} max={CAPTION_MAX} />}
        error={fieldError(errors, 'caption')}
        onChange={(e) => onChange({ caption: e.target.value })}
      />
      <Hashtags
        value={v.hashtags}
        onChange={(hashtags) => onChange({ hashtags })}
        error={fieldError(errors, 'hashtags')}
      />
    </>
  );
}

function ReelSection({ value: v, onChange, errors }: SectionProps<ReelValues>) {
  return (
    <>
      <TextArea
        label="Hook, script & shots"
        variant="filled"
        rows={5}
        value={v.script}
        error={fieldError(errors, 'script')}
        onChange={(e) => onChange({ script: e.target.value })}
      />
      <div className="grid gap-(--mbm-space-5) sm:grid-cols-2">
        <SegmentedControl
          label="Length"
          options={[
            { value: '15', label: '15 sec' },
            { value: '30', label: '30 sec' },
            { value: '60', label: '60 sec' },
          ]}
          value={String(v.durationSeconds) as '15' | '30' | '60'}
          onChange={(seconds) => onChange({ durationSeconds: Number(seconds) as 15 | 30 | 60 })}
        />
        <PlatformPicker
          value={v.platforms}
          onChange={(platforms) => onChange({ platforms })}
          error={fieldError(errors, 'platforms')}
        />
      </div>
      <TextArea
        label="Caption"
        variant="filled"
        value={v.caption}
        labelAside={<Counter used={v.caption.length} max={CAPTION_MAX} />}
        error={fieldError(errors, 'caption')}
        onChange={(e) => onChange({ caption: e.target.value })}
      />
      <Hashtags
        value={v.hashtags}
        onChange={(hashtags) => onChange({ hashtags })}
        error={fieldError(errors, 'hashtags')}
      />
    </>
  );
}

function WebsiteSection({ value: v, onChange, errors }: SectionProps<WebsiteValues>) {
  const setPage = (index: number, patch: Partial<WebsiteValues['pages'][number]>) =>
    onChange({ pages: v.pages.map((page, i) => (i === index ? { ...page, ...patch } : page)) });
  const pageCount = v.pages.length;
  const sectionCount = v.pages.reduce((n, page) => n + page.sections.length, 0);

  return (
    <>
      <TextArea
        label="Goal & audience"
        variant="filled"
        value={v.goalAudience}
        error={fieldError(errors, 'goalAudience')}
        onChange={(e) => onChange({ goalAudience: e.target.value })}
      />
      <div className="grid gap-(--mbm-space-3) sm:grid-cols-3">
        <Input
          label="Domain"
          variant="filled"
          placeholder="paradisebiryani.com"
          value={v.domainName}
          error={fieldError(errors, 'domainName')}
          onChange={(e) => onChange({ domainName: e.target.value })}
        />
        <Input
          label="Domain is with"
          variant="filled"
          placeholder="Client · GoDaddy"
          value={v.domainRegistrar}
          error={fieldError(errors, 'domainRegistrar')}
          onChange={(e) => onChange({ domainRegistrar: e.target.value })}
        />
        <Input
          label="Style"
          variant="filled"
          placeholder="Warm, traditional"
          value={v.style}
          error={fieldError(errors, 'style')}
          onChange={(e) => onChange({ style: e.target.value })}
        />
      </div>
      {/* TODO(api): the design lets you add a third colour; the table keeps a primary and a secondary. */}
      <div className="grid gap-(--mbm-space-3) sm:grid-cols-3">
        <Input
          label="Primary colour"
          variant="filled"
          placeholder="#7A1F1F"
          value={v.primaryColor}
          error={fieldError(errors, 'primaryColor')}
          onChange={(e) => onChange({ primaryColor: e.target.value })}
        />
        <Input
          label="Secondary colour"
          variant="filled"
          placeholder="#E8B04B"
          value={v.secondaryColor}
          error={fieldError(errors, 'secondaryColor')}
          onChange={(e) => onChange({ secondaryColor: e.target.value })}
        />
        <Input
          label="Fonts"
          variant="filled"
          placeholder="Playfair · Inter"
          value={v.fonts}
          error={fieldError(errors, 'fonts')}
          onChange={(e) => onChange({ fonts: e.target.value })}
        />
      </div>

      <div className="flex flex-col gap-(--mbm-space-3)">
        <Label
          aside={
            <span className="text-body-xsmall text-text-tertiary">
              {pageCount} {pageCount === 1 ? 'page' : 'pages'} · {sectionCount}{' '}
              {sectionCount === 1 ? 'section' : 'sections'}
            </span>
          }
        >
          Pages & sections
        </Label>
        {v.pages.map((page, index) => (
          <div
            key={index}
            className="flex flex-col gap-(--mbm-space-2) rounded-(--mbm-radius-md) bg-surface-muted p-(--mbm-space-3)"
          >
            <div className="flex items-center gap-(--mbm-space-2)">
              <Input
                aria-label={`Page ${index + 1} name`}
                variant="filled"
                placeholder="Page name, e.g. Home"
                value={page.title}
                error={fieldError(errors, `pages.${index}.title`)}
                containerClassName="flex-1"
                onChange={(e) => setPage(index, { title: e.target.value })}
              />
              <button
                type="button"
                aria-label={`Remove page ${index + 1}`}
                onClick={() => onChange({ pages: v.pages.filter((_, i) => i !== index) })}
                className="flex size-(--mbm-size-icon-button) items-center justify-center rounded-full text-text-secondary hover:text-text-primary focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
              >
                <Icon icon={X} size="md" />
              </button>
            </div>
            <TagInput
              value={page.sections}
              onChange={(sections) => setPage(index, { sections })}
              placeholder="Add a section and press Enter, e.g. Hero with offer"
            />
          </div>
        ))}
        <Button
          variant="ghost"
          size="small"
          icon={Plus}
          className="self-start"
          onClick={() => onChange({ pages: [...v.pages, { title: '', sections: [] }] })}
        >
          Add page
        </Button>
      </div>

      <TextArea
        label="Logo notes"
        variant="filled"
        rows={3}
        value={v.logoNotes}
        helperText="Which logo, where, what to avoid"
        error={fieldError(errors, 'logoNotes')}
        onChange={(e) => onChange({ logoNotes: e.target.value })}
      />
      <TextArea
        label="General notes"
        variant="filled"
        rows={3}
        value={v.generalNotes}
        error={fieldError(errors, 'generalNotes')}
        onChange={(e) => onChange({ generalNotes: e.target.value })}
      />
      <div className="flex flex-col gap-(--mbm-space-2)">
        <Label>Sites they like</Label>
        <TagInput
          value={v.referenceSites}
          onChange={(referenceSites) => onChange({ referenceSites })}
          placeholder="Paste a link and press Enter"
        />
        {fieldError(errors, 'referenceSites') ? (
          <p className="text-body-xsmall text-feedback-error">{fieldError(errors, 'referenceSites')}</p>
        ) : null}
      </div>
    </>
  );
}

function MonthlySection({ value: v, onChange, errors }: SectionProps<MonthlyValues>) {
  return (
    <>
      <div className="grid gap-(--mbm-space-3) sm:grid-cols-2">
        <Input
          label="Page"
          variant="filled"
          placeholder="e.g. Menu · paradisebiryani.com/menu"
          value={v.pageUrl}
          error={fieldError(errors, 'pageUrl')}
          onChange={(e) => onChange({ pageUrl: e.target.value })}
        />
        <Select
          label="Kind of change"
          variant="filled"
          value={v.changeKind}
          onChange={(e) => onChange({ changeKind: e.target.value as MonthlyValues['changeKind'] })}
        >
          {CHANGE_KINDS.map((kind) => (
            <option key={kind} value={kind}>
              {CHANGE_LABEL[kind]}
            </option>
          ))}
        </Select>
      </div>
      <TextArea
        label="What should change"
        variant="filled"
        rows={5}
        helperText="Be specific: section, old → new"
        value={v.description}
        error={fieldError(errors, 'description')}
        onChange={(e) => onChange({ description: e.target.value })}
      />
    </>
  );
}

function CustomSection({ value: v, onChange, errors }: SectionProps<CustomValues>) {
  return (
    <TextArea
      label="What's needed"
      variant="filled"
      rows={6}
      value={v.details}
      error={fieldError(errors, 'details')}
      onChange={(e) => onChange({ details: e.target.value })}
    />
  );
}

export interface BriefFormProps {
  values: TaskFormValues;
  onChange: (patch: Partial<TaskFormValues>) => void;
  errors: TaskFormErrors;
  /** The files control sits between the typed fields and the publish row, as in the design. */
  assets: ReactNode;
}

/** Step 2 of the new-task page: the title and the brief fields for the chosen type (Figma A05.3). */
export function BriefForm({ values, onChange, errors, assets }: BriefFormProps) {
  const patch =
    <K extends 'post' | 'reel' | 'website' | 'monthly' | 'custom'>(key: K) =>
    (change: Partial<TaskFormValues[K]>) =>
      onChange({ [key]: { ...values[key], ...change } } as Partial<TaskFormValues>);

  return (
    <div className="flex flex-col gap-(--mbm-space-5)">
      <Input
        label="Title"
        variant="filled"
        autoComplete="off"
        value={values.title}
        error={errors.title}
        onChange={(e) => onChange({ title: e.target.value })}
      />

      {values.type === 'post' ? <PostSection value={values.post} onChange={patch('post')} errors={errors} /> : null}
      {values.type === 'reel' ? <ReelSection value={values.reel} onChange={patch('reel')} errors={errors} /> : null}
      {values.type === 'website' ? (
        <WebsiteSection value={values.website} onChange={patch('website')} errors={errors} />
      ) : null}
      {values.type === 'monthly_update' ? (
        <MonthlySection value={values.monthly} onChange={patch('monthly')} errors={errors} />
      ) : null}
      {values.type === 'custom' ? (
        <CustomSection value={values.custom} onChange={patch('custom')} errors={errors} />
      ) : null}

      {assets}

      {values.type === 'post' ? (
        <PublishRow
          date={values.post.publishDate}
          time={values.post.publishTime}
          audioNote={values.post.audioNote}
          onChange={patch('post')}
          errors={errors}
        />
      ) : null}
      {values.type === 'reel' ? (
        <PublishRow
          date={values.reel.publishDate}
          time={values.reel.publishTime}
          audioNote={values.reel.audioNote}
          onChange={patch('reel')}
          errors={errors}
        />
      ) : null}
      {values.type === 'website' || values.type === 'monthly_update' ? (
        <div className="grid gap-(--mbm-space-3) border-t border-border-line pt-(--mbm-space-5) sm:grid-cols-2">
          <DateField
            label="Live by"
            value={values.type === 'website' ? values.website.liveBy : values.monthly.liveBy}
            error={fieldError(errors, 'liveBy')}
            onChange={(liveBy) =>
              values.type === 'website' ? patch('website')({ liveBy }) : patch('monthly')({ liveBy })
            }
          />
          <p className="self-end pb-(--mbm-space-3) text-body-xsmall text-text-tertiary">
            Built on staging first. The client approves the preview before it goes live.
          </p>
        </div>
      ) : null}
    </div>
  );
}
